/**
 * Search Filter Component for Review Page
 * 
 * Provides client-side search functionality for translation content
 * without server-side database queries.
 */

import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Search,
  X,
  Filter,
  Download
} from 'lucide-react'
import Button from '../ui/Button'

const SearchFilter = ({ 
  searchValue = '', 
  onSearchChange, 
  onClearSearch,
  onExportFiltered,
  resultCount,
  totalCount,
  isExportEnabled = false,
  placeholder,
  className = ''
}) => {
  const { t } = useTranslation()
  const [localValue, setLocalValue] = useState(searchValue)
  const inputRef = useRef(null)
  const debounceRef = useRef(null)

  // Debounce search to avoid excessive filtering
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    
    debounceRef.current = setTimeout(() => {
      if (localValue !== searchValue) {
        onSearchChange(localValue)
      }
    }, 300)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [localValue, searchValue, onSearchChange])

  // Sync with external value changes
  useEffect(() => {
    if (searchValue !== localValue) {
      setLocalValue(searchValue)
    }
  }, [searchValue])

  const handleClear = () => {
    setLocalValue('')
    onClearSearch()
    inputRef.current?.focus()
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClear()
    }
  }

  const isFiltered = searchValue.length > 0
  const hasResults = resultCount !== undefined && totalCount !== undefined

  return (
    <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${className}`}>
      {/* Search Input */}
      <div className="flex-1 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || t('review.search.placeholder')}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          />
          {localValue && (
            <button
              onClick={handleClear}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              title={t('review.search.clear')}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Results Summary and Actions */}
      <div className="flex items-center space-x-3">
        {/* Search Results Summary */}
        {hasResults && (
          <div className="flex items-center space-x-2 text-sm">
            {isFiltered ? (
              <div className="flex items-center space-x-1 text-blue-600 dark:text-blue-400">
                <Filter className="w-4 h-4" />
                <span>
                  {t('review.search.results', { count: resultCount, total: totalCount })}
                </span>
              </div>
            ) : (
              <div className="text-gray-600 dark:text-gray-400">
                {t('review.search.totalItems', { total: totalCount })}
              </div>
            )}
          </div>
        )}

        {/* Export Filtered Data */}
        {isExportEnabled && isFiltered && resultCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onExportFiltered('csv')}
            className="flex items-center space-x-2"
            title={t('review.search.exportFiltered')}
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">
              {t('review.search.export')}
            </span>
          </Button>
        )}

        {/* Clear Search */}
        {isFiltered && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <X className="w-4 h-4" />
            <span className="hidden sm:inline">
              {t('review.search.clear')}
            </span>
          </Button>
        )}
      </div>
    </div>
  )
}

export default SearchFilter